import { type MaybeRefOrGetter, type Ref, computed, toValue, watch } from 'vue'

import type { Cart } from '@tix/entity'

import {
  type DonationEventTemplate,
  type DonationGa4Context,
  type DonationTicketGroup,
  type DonationTicketType,
  buildDonationCartPayload,
  buildDonationViewPayload,
  trackDonationPurchase,
} from '@/helpers/donationGa4'
import type { DonationOptionsValues } from '@/helpers/donationSurvey'
import { getEnv } from '@/helpers/environment'
import { triggerGA4Event } from '@/helpers/ga4'

/**
 * Emits GA4 donation funnel events from the donation identity fields plus the live cart.
 *
 * Readiness is split so a page view isn't coupled to payment state:
 * - The identity (`eventTemplate` + `donationGroup` + at least one `ticketType`) is enough for `view_item`; it's available as soon as the event loads.
 * - `paymentReady` (the cart is booked with a Stripe fee) additionally gates `add_to_cart` / `begin_checkout` / purchase.
 *
 * Lifecycle:
 * - `viewItem` fires `view_item` once the identity is present (event loaded)
 * - `add_to_cart` when the reserved amount identity changes (ticket type + face value), once `paymentReady`
 * - `beginCheckout` fires `begin_checkout` once, when the host reports identity / Stripe form touch (web-client CheckoutRoute `focusin` / TixPayment `@focus`). Express Checkout has no Stripe `focus` event; wallet `click` is forwarded as that same host `focus`.
 * - `trackPurchase` fires `begin_checkout` first if it hasn't yet (web-client onSubmit / onSuccess fallback), then the in-page purchase
 *
 * Redirect payment methods settle on {@link PaymentReturnPage} instead, which rebuilds the purchase payload from the cart.
 */
export function useDonationGa4Tracking(params: {
  eventTemplate: DonationEventTemplate | null
  donationGroup: DonationTicketGroup | null
  ticketTypes: DonationTicketType[]
  paymentReady: MaybeRefOrGetter<boolean>
  cart: Ref<Cart | null>
  donationOptions: Ref<DonationOptionsValues>
}): { viewItem: () => void; beginCheckout: () => void; trackPurchase: (transactionId: string) => void } {
  const { eventTemplate, donationGroup, ticketTypes, paymentReady, cart, donationOptions } = params
  const currency = getEnv().portal.default_currency_code

  let beganCheckout = false
  let lastAmountKey: string | null = null

  // Donation identity is static once the event loads. Present here mirrors the page's `eventReady`: an event template, its donation group, and at least one reservable ticket type.
  const ctx: DonationGa4Context | null =
    eventTemplate && donationGroup && ticketTypes.length > 0 ? { eventTemplate, donationGroup, ticketTypes } : null

  const isPaymentReady = computed(() => toValue(paymentReady))
  const donationTicket = computed(() => cart.value?.tickets?.[0])
  const amountKey = computed(() => {
    const ticket = donationTicket.value
    if (!ticket) return null
    return `${ticket.ticket_type_id}:${ticket.face_value ?? ''}`
  })

  /*
   * Amount-reserve watcher — not form-touch.
   *
   * Fires `add_to_cart` whenever the reserved donation amount identity changes (`ticket_type_id` + `face_value`), once payment is ready. Clears the last-seen key when the cart loses its ticket so a later re-reserve can fire again.
   */
  watch([isPaymentReady, amountKey], ([ready, key]) => {
    if (!ready || !ctx) return

    if (!key) return (lastAmountKey = null)
    if (key === lastAmountKey) return
    lastAmountKey = key

    const ticket = donationTicket.value
    if (!ticket) return

    triggerGA4Event('add_to_cart', buildDonationCartPayload(ctx, ticket, ticket.currency_code || currency))
  })

  function viewItem(): void {
    if (!ctx) return

    triggerGA4Event('view_item', buildDonationViewPayload(ctx, currency))
  }

  /** Once per session. No-op until payment is ready and a donation ticket is reserved. */
  function beginCheckout(): void {
    if (beganCheckout || !ctx || !isPaymentReady.value) return

    const ticket = donationTicket.value
    if (!ticket) return

    beganCheckout = true
    triggerGA4Event('begin_checkout', buildDonationCartPayload(ctx, ticket, ticket.currency_code || currency))
  }

  function trackPurchase(transactionId: string): void {
    if (!ctx || !isPaymentReady.value) return

    const ticket = donationTicket.value
    if (!ticket) return

    beginCheckout()
    trackDonationPurchase(ctx, ticket, donationOptions.value, ticket.currency_code || currency, transactionId)
  }

  return { viewItem, beginCheckout, trackPurchase }
}
